import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TenantSignup } from '@hospasense/contracts';
import { useApproveSignup, useRejectSignup } from '../lib/queries.js';
import { Button, Input, Modal, Textarea, useToast } from '../components/ui/index.js';

/**
 * Approve or reject a hospital signup.
 *
 * Approving CREATES A CUSTOMER, so this is a deliberate dialogue and not an
 * inline button: it states what will happen, requires a written reason, and
 * shows the operator exactly what the hospital typed before they commit to it.
 *
 * The reason is not ceremony. It lands on the signup row and in the
 * tamper-evident audit trail, and it is what answers "why is this company a
 * customer?" six months later when nobody remembers the phone call.
 *
 * Uses the shared Modal rather than a hand-rolled backdrop: it already traps
 * focus, restores it on close and handles Escape. The first version of this
 * file reimplemented the shell with class names that do not exist in this
 * codebase — it typechecked perfectly and would have rendered unstyled.
 */

const MIN_REASON = 10;

export function SignupDecisionDialog({
  signup,
  mode,
  onClose,
}: {
  signup: TenantSignup;
  mode: 'approve' | 'reject';
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [name, setName] = useState(signup.organisationName);
  const approve = useApproveSignup(signup.id);
  const reject = useRejectSignup(signup.id);
  const mutation = mode === 'approve' ? approve : reject;

  const reasonTooShort = reason.trim().length < MIN_REASON;

  const submit = (): void => {
    if (reasonTooShort) return;
    if (mode === 'approve') {
      approve.mutate(
        {
          reason: reason.trim(),
          ...(name.trim() && name.trim() !== signup.organisationName
            ? { organisationName: name.trim() }
            : {}),
        },
        {
          onSuccess: (result) => {
            toast.success(`${name.trim() || signup.organisationName} is now a customer.`);
            onClose();
            /**
             * Go straight to the customer that now exists.
             *
             * Approving and being dropped back on a list is how an operator
             * ends up unsure whether it worked and clicks Approve again.
             */
            if (result.organisationId) navigate(`/organisations/${result.organisationId}`);
          },
        },
      );
      return;
    }
    reject.mutate(
      { reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`Signup from ${signup.organisationName} rejected.`);
          onClose();
        },
      },
    );
  };

  const declaredSize = [
    signup.declaredBedCount === null
      ? 'beds not stated'
      : `${signup.declaredBedCount} beds declared`,
    signup.declaredDailyOpdVolume !== null
      ? `${signup.declaredDailyOpdVolume} OPD/day declared`
      : null,
    signup.staffingShape ? `${signup.staffingShape} staffing` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      open
      onClose={onClose}
      title={`${mode === 'approve' ? 'Approve' : 'Reject'} ${signup.organisationName}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant={mode === 'approve' ? 'primary' : 'danger'}
            onClick={submit}
            disabled={reasonTooShort}
            loading={mutation.isPending}
          >
            {mode === 'approve' ? 'Approve and create customer' : 'Reject signup'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-text-secondary">
          {mode === 'approve' ? (
            <>
              This creates an organisation in the customer estate, linked to this signup. It starts
              in the <strong className="text-text-primary">prospect</strong> lifecycle state.
            </>
          ) : (
            <>
              This records a rejection. No organisation is created, and nothing is sent to the
              hospital from here.
            </>
          )}
        </p>

        {/*
          What the hospital actually typed, at the point of decision. Every
          value is labelled "declared" — these are form entries, and one live
          signup claims 120 beds with a single member of staff.
        */}
        <dl className="grid grid-cols-[9rem_1fr] gap-x-3 gap-y-2 border-t border-border pt-3 text-sm">
          <dt className="text-text-muted">Contact</dt>
          <dd className="text-text-primary">
            {signup.contactName}{' '}
            <a className="text-text-muted underline" href={`mailto:${signup.contactEmail}`}>
              {signup.contactEmail}
            </a>
          </dd>
          <dt className="text-text-muted">Declared size</dt>
          <dd className="text-text-primary">{declaredSize}</dd>
          <dt className="text-text-muted">Hosting preference</dt>
          <dd className="text-text-primary">{signup.deploymentPreference.replace(/_/g, ' ')}</dd>
          <dt className="text-text-muted">Modules requested</dt>
          <dd className="text-xs text-text-secondary">
            {signup.requestedModules.join(', ') || '—'}
          </dd>
        </dl>

        {mode === 'approve' ? (
          /*
            Hospitals type their own name into a public form, so "St. Mary's"
            and "St Marys Hospital Pvt Ltd" both arrive. Correcting it here is
            cheaper than creating a customer with a bad name and renaming it.
          */
          <Input
            label="Organisation name"
            hint="As typed by the hospital. Correct it here rather than renaming the customer later."
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
          />
        ) : null}

        <Textarea
          label={`Reason ${mode === 'approve' ? '(recorded against the customer)' : '(recorded)'}`}
          required
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder={
            mode === 'approve'
              ? 'e.g. Verified by phone with the hospital administrator on 21 Sep.'
              : 'e.g. Duplicate of an existing customer account, confirmed with the contact.'
          }
          {...(reasonTooShort && reason.length > 0
            ? {
                error: `Give a reason of at least ${MIN_REASON} characters — this is the permanent record of why.`,
              }
            : {})}
        />

        {mutation.isError ? (
          <div
            className="rounded border border-error/40 bg-error/10 p-2 text-xs text-error"
            role="alert"
          >
            {/*
              The API's message, verbatim. A 409 here means somebody else
              already decided this signup, and "something went wrong" would
              leave the operator clicking Approve at a customer that exists.
            */}
            {(mutation.error as Error).message}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
